import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    IAchievement,
    IDashboardData,
    ILearningPath,
    IRecentActivity,
    ISkillProgress,
    IStudyGoal,
    IUserStats,
    IWeeklyReport,
} from '@/features/student/StudentDashboard/interfaces';

interface IDashboardState {
    dashboardData: IDashboardData;
    userStats: IUserStats;
    skillProgress: ISkillProgress[];
    recentActivities: IRecentActivity[];
    studyGoals: IStudyGoal[];
    learningPaths: ILearningPath[];
    achievements: IAchievement[];
    weeklyReport: IWeeklyReport;
    isLoading: boolean;
    // False until the first dashboard response (or error), so the cards show skeletons instead of
    // empty values on the very first render, before the fetch is even dispatched.
    isLoaded: boolean;
    error: string | null;
}

const initialState: IDashboardState = {
    dashboardData: {
        userStats: {},
        skillProgress: [],
        recentActivities: [],
        studyGoals: [],
        learningPaths: [],
        weeklyReport: {},
        todayAiLessons: [],
    },
    userStats: {},
    skillProgress: [],
    recentActivities: [],
    studyGoals: [],
    learningPaths: [],
    achievements: [],
    weeklyReport: {},
    isLoading: false,
    isLoaded: false,
    error: null,
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        // Fetch Dashboard Data Actions
        fetchDashboardDataRequest: (state: IDashboardState) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchDashboardDataSuccess: (state: IDashboardState, action: PayloadAction<IDashboardData>) => {
            state.isLoading = false;
            state.isLoaded = true;
            state.dashboardData = action.payload;
            state.userStats = action.payload.userStats;
            state.skillProgress = action.payload.skillProgress;
            state.recentActivities = action.payload.recentActivities;
            state.studyGoals = action.payload.studyGoals;
            state.learningPaths = action.payload.learningPaths;
            state.weeklyReport = action.payload.weeklyReport;
            state.error = null;
        },
        fetchDashboardDataFailure: (state: IDashboardState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.isLoaded = true;
            state.error = action.payload;
        },

        // Fetch User Stats Actions
        fetchUserStatsRequest: (state: IDashboardState) => {
            state.isLoading = true;
            state.error = null;
        },

        // Utility Actions
        clearError: (state: IDashboardState) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Global reset action - clears all dashboard state
            .addCase(globalReset, (state) => {
                Object.assign(state, initialState);
            });
    },
});

export const { fetchDashboardDataRequest, fetchDashboardDataSuccess, fetchDashboardDataFailure, clearError } =
    dashboardSlice.actions;

export default dashboardSlice.reducer;
